import { useCallback, useMemo } from "react"
import { Box, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material"
import { useNavigate } from "react-router-dom"

import {
  CURRENT_EVENT_FILTER_VALUE,
  UPCOMING_EVENT_FILTER_VALUE,
} from "constant"

import { getIcon } from "utils/icon"
import { useAppUI } from "providers/ui"
import { useTranslation } from "providers/i18n"
import { useEvent } from "hooks/event/useEvent"

import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-v"
import { Carousel, CarouselItem } from "components/Carousel"
import { Head } from "components/Head"
import { EmptyData } from "components/EmptyData"
import { CalendarStarIcon } from "components/icons"
import { VerticalEventCard } from "components/domains/events"
import { VenueSelector } from "components/VenueSelector"
import { LanguageButton } from "components/Language"
import { SearchToggleButton } from "components/domains/search"
import { MotionDiv, showContent } from "components/motions"
import { LanguageSelectMenu } from "components/Language/LanguageSelectMenu"

export const EventsKioskVPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
  additionalMenu = null,
}) => {
  const { openSearch } = useAppUI()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const {
    isMultiVenueProject,
    venueFilter,
    venues,
    locationVenueId,
    groupEvents,
    activeGroupEvent,
    handleChange,
    changeVenueFilter,
  } = useEvent()

  const theme = useTheme()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.events) || <CalendarStarIcon />,
    [theme]
  )
  const { styleOverrides } = theme.components?.[`EventPage`] || {}
  const { eventCarouselOption } =
    theme.components[`EventPage`]?.defaultProps || {}
  const {
    root,
    carouselContainer,
    carousel,
    eventGroupSelectorButton,
    emptyDataWrapper,
    navigationWrapper,
    venueSelector,
    toolbarContainer,
    actionButton,
    actionContainer,
    searchBarContainer,
    languageSelectMenu,
  } = styleOverrides
  const activeEvents = groupEvents[activeGroupEvent]
  const slidePerPage = 3
  const carouselType =
    activeEvents && activeEvents.length < slidePerPage ? "slide" : "loop"
  const autoplay = activeEvents && activeEvents.length > 3
  const focus = activeEvents.length < 3 ? "center" : eventCarouselOption.focus

  const handleClickSlide = useCallback(
    (_splide, { slide }, _event) => {
      navigate(slide.getAttribute("link"))
    },
    [navigate]
  )

  return (
    <>
      <Head title={t("Events & Activities")} />
      <Layout>
        <Box sx={root}>
          <MotionDiv
            animate="animate"
            initial="initial"
            variants={showContent}
            sx={carouselContainer}
          >
            {!activeEvents || activeEvents.length <= 0 ? (
              <Box sx={emptyDataWrapper}>
                <EmptyData
                  icon={emptyIcon}
                  primary={
                    activeGroupEvent === CURRENT_EVENT_FILTER_VALUE
                      ? t(`No Current Events`)
                      : t(`No Upcoming Events`)
                  }
                  secondary={t("Check Back Soon")}
                />
              </Box>
            ) : (
              <Carousel
                onClick={handleClickSlide}
                options={{
                  type: carouselType,
                  perPage: slidePerPage,
                  pagination: true,
                  trimSpace: false,
                  autoplay,
                  gap: "0",
                  ...eventCarouselOption,
                  focus,
                }}
                sx={carousel}
              >
                {activeEvents.map((event) => {
                  const sponsoredId = event?.properties?.sponsoredId
                  return (
                    <CarouselItem
                      link={`/events/${event.id}`}
                      key={`${sponsoredId || ""}${event.id}`}
                    >
                      <VerticalEventCard dense={true} event={event} />
                    </CarouselItem>
                  )
                })}
              </Carousel>
            )}

            <Box sx={eventGroupSelectorButton}>
              <ToggleButtonGroup
                value={activeGroupEvent}
                onChange={handleChange}
                exclusive
              >
                <ToggleButton value={CURRENT_EVENT_FILTER_VALUE}>
                  {t("Current Events")}
                </ToggleButton>
                <ToggleButton value={UPCOMING_EVENT_FILTER_VALUE}>
                  {t("Upcoming Events")}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </MotionDiv>
          {isMultiVenueProject && (
            <Box sx={venueSelector}>
              <VenueSelector
                selectedVenue={venueFilter}
                venues={venues}
                onClickVenue={changeVenueFilter}
                currentVenue={locationVenueId}
              />
            </Box>
          )}
          <Box key="app-navigation" sx={navigationWrapper}>
            <Box sx={toolbarContainer}>
              <Box sx={actionContainer}>
                <LanguageSelectMenu sx={languageSelectMenu} />
                <LanguageButton variant="contained" sx={actionButton} />
                {/* //TODO:: Add a wheel chair's mode handler function later */}
                {/*  <Button
            variant="contained"
            sx={actionButton}
            onClick={() => {}}
            startIcon={<RestroomWheelchairIcon />}
          /> */}
                {additionalMenu}
                {/* //TODO:: Create Toolbar component and refactor Code */}
              </Box>
              <Box sx={searchBarContainer}>
                <SearchToggleButton
                  placeholder={t("Search shops and more...")}
                  onClick={openSearch}
                />
              </Box>
            </Box>
            {AppNavigation && <AppNavigation />}
          </Box>
        </Box>
      </Layout>
    </>
  )
}
